import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'localization';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { Channel, ChannelScope } from 'types/channel';
import { UseCaseRow } from 'types/schema';
import { MarketplaceChannelDocument } from 'types/marketplace';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import {
  listChannels,
  ListChannelsResponse,
  MarketplaceSearchFilters,
} from '../../../api/marketplaceApi';
import { useMarketplaceSearch } from '../../../hooks/useMarketplaceSearch';
import { useMarketplaceMetrics } from '../../../hooks/useMarketplaceMetrics';
import { useSubscription } from '../../../hooks/useSubscription';
import { ChannelCardMolecule, ChannelCardDensity } from '../../molecules/marketplace/ChannelCardMolecule';
import FeaturedHeroCarouselMolecule from '../../molecules/marketplace/FeaturedHeroCarouselMolecule';
import MarketplaceFilterBarMolecule, {
  MarketplaceFilterBarMoleculeLabels,
  MarketplaceSortOption,
  SubscriptionStateFilter,
} from '../../molecules/marketplace/MarketplaceFilterBarMolecule';
import MarketplaceHeaderMolecule, {
  MarketplaceHeaderMoleculeLabels,
} from '../../molecules/marketplace/MarketplaceHeaderMolecule';
import { ScopeFilterValue } from '../../molecules/marketplace/ScopeFilterMolecule';
import {
  deriveSubscribeButtonState,
  SubscribeButtonState,
} from '../../atoms/marketplace/SubscribeButtonAtom';
import { CreateChannelDialogOrganism } from './CreateChannelDialogOrganism';
import SearchResultsOrganism from './SearchResultsOrganism';
import './MarketplaceHomeOrganism.scss';
import './marketplace-shared.scss';

export type ListedChannel = ListChannelsResponse['channels'][number];

/** Injected public data source for unauthenticated marketplace browsing (EAINT-663). */
export interface MarketplacePublicDataSource {
  channels: ListedChannel[];
  total: number;
  loading: boolean;
  error: string | null;
}

export interface MarketplaceHomeOrganismProps {
  useCases?: UseCaseRow[];
  useCasesLoading?: boolean;
  useCasesError?: string | null;
  /** When provided, bypasses the authenticated fetch path and uses public data. */
  dataSource?: MarketplacePublicDataSource;
}

const PAGE_SIZE = 40;
const FEATURED_LIMIT = 10;

// The list endpoint has no 'relevance' sort; fall back to 'recent' so the UI
// option stays consistent with the search path (where 'relevance' is valid).
const SORT_LIST_MAP: Record<
  MarketplaceSortOption,
  'rating' | 'subscribers' | 'recent' | 'name'
> = {
  subscribers: 'subscribers',
  rating: 'rating',
  recent: 'recent',
  relevance: 'recent',
};

function docToChannel(doc: MarketplaceChannelDocument): Channel {
  return {
    id: doc.id,
    system_name: doc.id,
    name: { default: doc.name },
    description: { default: doc.description },
    owner_sphere_id: doc.scope_id,
    owner_tenant_id: doc.organization_id,
    scope: doc.scope,
    scope_id: doc.scope_id,
    visibility: doc.visibility,
    status: doc.status,
    is_system: doc.is_system,
    reviewed_by: null,
    reviewed_at: null,
    rejection_reason: null,
    created_by: '',
    created_at: doc.created_at,
    updated_at: doc.updated_at,
    use_case_tags: doc.use_case_tags,
  };
}

const MarketplaceHomeOrganism: React.FC<MarketplaceHomeOrganismProps> = ({
  useCases = [],
  useCasesLoading = false,
  useCasesError = null,
  dataSource,
}) => {
  const isPublicMode = !!dataSource;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const initialQuery = searchParams.get('q') ?? '';

  const [searchInput, setSearchInput] = useState(initialQuery);
  const [sort, setSort] = useState<MarketplaceSortOption>('subscribers');
  const [density, setDensity] = useState<ChannelCardDensity>('roomy');
  const [scope, setScope] = useState<ScopeFilterValue>('all');
  const [subState, setSubState] =
    useState<SubscriptionStateFilter>('all');
  const [useCaseTags, setUseCaseTags] = useState<string[]>([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const isOwnedFilter = subState === 'owned';

  const [listChannelsResult, setListChannelsResult] = useState<
    ListedChannel[]
  >([]);
  const [listTotal, setListTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [listRefetchTrigger, setListRefetchTrigger] = useState(0);
  const [ownedCount, setOwnedCount] = useState(0);

  const isSearching = searchInput.trim().length > 0;

  const searchFilters = useMemo<MarketplaceSearchFilters>(() => {
    // No docType filter — we want channels + items + publishers in the same
    // result set so the SearchResultsOrganism can render the three groups.
    // The `scope` filter only applies to channel docs (items/publishers don't
    // carry a scope field), so we only forward it when the user is also
    // filtering by scope; the result will be channel-heavy when scope is set,
    // which matches expectations.
    const next: MarketplaceSearchFilters = {};
    if (useCaseTags.length > 0) {
      next.useCaseTags = useCaseTags;
    }
    if (scope !== 'all') {
      next.scope = scope;
    }
    return next;
  }, [useCaseTags, scope]);

  const {
    results: searchResults,
    total: searchTotal,
    facets: searchFacets,
    loading: searchLoading,
    error: searchError,
    search,
    setFilters: setSearchFilters,
    setSort: setSearchSort,
    loadMore: searchLoadMore,
    hasMore: searchHasMore,
  } = useMarketplaceSearch();

  const initialSearchDone = useRef(false);
  useEffect(() => {
    if (initialQuery && !initialSearchDone.current) {
      initialSearchDone.current = true;
      search(initialQuery);
    }
  }, [initialQuery, search]);

  useEffect(() => {
    setSearchFilters(searchFilters);
  }, [searchFilters, setSearchFilters]);

  useEffect(() => {
    setSearchSort(sort);
  }, [sort, setSearchSort]);

  useEffect(() => {
    if (isPublicMode || isSearching) {
      return undefined;
    }
    if (!apiClients?.schemaClient || !organizationId) {
      return undefined;
    }

    let cancelled = false;
    setListLoading(true);
    setListError(null);

    const useCaseTagParam =
      useCaseTags.length > 0 ? useCaseTags : undefined;

    const filters = {
      scope: scope === 'all' ? undefined : (scope as ChannelScope),
      subscribed: (() => {
        if (subState === 'subscribed') return true;
        if (subState === 'available') return false;
        return undefined;
      })(),
      useCaseTag: useCaseTagParam,
      mine: isOwnedFilter ? true : undefined,
      sortBy: SORT_LIST_MAP[sort],
      limit: PAGE_SIZE,
    };

    listChannels(apiClients.schemaClient, filters)
      .then((response) => {
        if (cancelled) return;
        setListChannelsResult(response.channels);
        setListTotal(response.total);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err instanceof Error ? err.message : 'Failed to load channels';
        setListError(message);
      })
      .finally(() => {
        if (cancelled) return;
        setListLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    apiClients,
    organizationId,
    isSearching,
    scope,
    subState,
    isOwnedFilter,
    sort,
    useCaseTags,
    listRefetchTrigger,
  ]);

  const refetchList = useCallback(() => {
    setListRefetchTrigger((prev) => prev + 1);
  }, []);

  const [featuredChannels, setFeaturedChannels] = useState<
    ListedChannel[]
  >([]);
  useEffect(() => {
    if (isPublicMode || !apiClients?.schemaClient || !organizationId) {
      return undefined;
    }
    let cancelled = false;
    listChannels(apiClients.schemaClient, {
      limit: FEATURED_LIMIT,
      sortBy: 'subscribers',
    })
      .then((response) => {
        if (cancelled) return;
        // Mirror the design's hero pick (v3-publishers HTML hardcodes the
        // center-channel): prefer explicitly is_featured channels, otherwise
        // fall back to the lone system channel (Center Channel) so the hero
        // always shows in a fresh org without manual data steps.
        const featured = response.channels.filter(
          (channel) => channel.is_featured === true,
        );
        if (featured.length > 0) {
          setFeaturedChannels(featured);
        } else {
          setFeaturedChannels(
            response.channels.filter((channel) => channel.is_system === true),
          );
        }
      })
      .catch(() => {
        if (cancelled) return;
        setFeaturedChannels([]);
      });
    return () => {
      cancelled = true;
    };
  }, [apiClients, organizationId]);

  // Dedicated owned-channel count for the "My channels" badge — fetched
  // independently of the active view so it reflects ALL channels the user owns
  // (including unpublished/draft), not just those in the current (published)
  // listing. mine=true makes the backend return every owned channel.
  useEffect(() => {
    if (!apiClients?.schemaClient || !organizationId) return undefined;
    let cancelled = false;
    listChannels(apiClients.schemaClient, { mine: true, limit: 1 })
      .then((response) => {
        if (!cancelled) setOwnedCount(response.total);
      })
      .catch(() => {
        if (!cancelled) setOwnedCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [apiClients, organizationId, listRefetchTrigger]);

  const displayedChannels = useMemo<ListedChannel[]>(() => {
    if (isPublicMode) {
      return dataSource!.channels;
    }
    const list: ListedChannel[] = isSearching
      ? searchResults
          .filter(
            (doc): doc is MarketplaceChannelDocument =>
              doc.doc_type === 'channel',
          )
          .map((doc) => ({
            ...docToChannel(doc),
            is_subscribed: undefined,
            is_mandatory: undefined,
            item_count: doc.item_count,
            subscriber_count: doc.subscriber_count,
          }))
      : listChannelsResult;

    // Pin the Center Channel (the lone system channel) to the top so it is
    // always the first card regardless of the current sort/search ranking.
    // Stable secondary order: preserve the upstream order for everything else.
    return [...list].sort((a, b) => {
      const aSys = a.is_system ? 1 : 0;
      const bSys = b.is_system ? 1 : 0;
      return bSys - aSys;
    });
  }, [isPublicMode, dataSource, isSearching, searchResults, listChannelsResult]);

  const displayedTotal = isPublicMode
    ? dataSource!.total
    : isSearching
      ? searchTotal
      : listTotal;
  const isLoading = isPublicMode
    ? dataSource!.loading
    : isSearching
      ? searchLoading
      : listLoading;
  const errorMessage = isPublicMode
    ? dataSource!.error
    : isSearching
      ? searchError
      : listError;

  const { metricsMap, fetchMetrics } = useMarketplaceMetrics();
  useEffect(() => {
    const targets = [
      ...featuredChannels.map((c) => ({
        targetType: 'channel' as const,
        targetId: c.id,
      })),
      ...displayedChannels.map((c) => ({
        targetType: 'channel' as const,
        targetId: c.id,
      })),
    ];
    if (targets.length > 0) {
      fetchMetrics(targets);
    }
  }, [featuredChannels, displayedChannels, fetchMetrics]);

  const { toggleSubscription, isToggling } = useSubscription();

  useEffect(() => {
    document.title = isSearching
      ? `"${searchInput}" - ${t('marketplace.landing.title')} - Center`
      : `${t('marketplace.landing.title')} - Center`;
  }, [isSearching, searchInput, t]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      search(value);
      if (value.trim().length > 0) {
        setSearchParams({ q: value }, { replace: true });
      } else {
        setSearchParams({}, { replace: true });
      }
    },
    [search, setSearchParams],
  );

  const handleChannelClick = useCallback(
    (channelId: string) => {
      navigate(`/marketplace/channels/${channelId}`);
    },
    [navigate],
  );

  const handleSubscribeToggle = useCallback(
    (channelId: string, isCurrentlySubscribed: boolean) => {
      toggleSubscription(channelId, isCurrentlySubscribed).then((success) => {
        if (success) {
          refetchList();
        }
      });
    },
    [toggleSubscription, refetchList],
  );

  const handleClearFilters = useCallback(() => {
    setScope('all');
    setSubState('all');
    setUseCaseTags([]);
  }, []);

  const handleCreateClick = useCallback(() => {
    setShowCreateDialog(true);
  }, []);

  const handleChannelCreated = useCallback(() => {
    setShowCreateDialog(false);
    refetchList();
  }, [refetchList]);

  const handleCloseCreateDialog = useCallback(() => {
    setShowCreateDialog(false);
  }, []);

  const ratingAverages = useMemo<Record<string, number>>(() => {
    const out: Record<string, number> = {};
    for (const channel of featuredChannels) {
      const metrics = metricsMap[`channel:${channel.id}`];
      if (metrics && metrics.rating_average > 0) {
        out[channel.id] = metrics.rating_average;
      }
    }
    return out;
  }, [featuredChannels, metricsMap]);

  const ratingCountLabels = useMemo<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const channel of featuredChannels) {
      const metrics = metricsMap[`channel:${channel.id}`];
      if (metrics && metrics.rating_count > 0) {
        out[channel.id] = t('marketplace.ratings.ratingCount', {
          count: metrics.rating_count,
        });
      }
    }
    return out;
  }, [featuredChannels, metricsMap, t]);

  const filterBarLabels: MarketplaceFilterBarMoleculeLabels = useMemo(
    () => ({
      searchPlaceholder: t('marketplace.landing.searchPlaceholder'),
      searchAriaLabel: t('marketplace.landing.searchAriaLabel'),
      sortAriaLabel: t('marketplace.landing.sortAriaLabel'),
      sortOptions: {
        subscribers: t('marketplace.landing.sort.subscribers'),
        rating: t('marketplace.landing.sort.rating'),
        recent: t('marketplace.landing.sort.recent'),
        relevance: t('marketplace.landing.sort.relevance'),
      },
      density: {
        roomy: t('marketplace.landing.view.roomy'),
        list: t('marketplace.landing.view.list'),
      },
      densityAriaLabel: t('marketplace.landing.densityAriaLabel'),
      searchKbdHint: t('marketplace.landing.searchKbdHint'),
      subStateAriaLabel: t('marketplace.landing.subStateAriaLabel'),
      subState: {
        all: t('marketplace.landing.subState.all'),
        subscribed: t('marketplace.landing.subState.subscribed'),
        available: t('marketplace.landing.subState.available'),
        owned: t('marketplace.landing.subState.owned'),
      },
    }),
    [t],
  );

  const heroLabels = useMemo(
    () => ({
      featured: t('marketplace.landing.featured'),
      prev: t('marketplace.landing.hero.prev'),
      next: t('marketplace.landing.hero.next'),
      goToSlide: (index: number) =>
        t('marketplace.landing.hero.goToSlide', { index: index + 1 }),
      required: t('marketplace.landing.hero.required'),
      subsItemsLabel: (subs: number, items: number) =>
        t('marketplace.common.statsLine', {
          subs: t('marketplace.common.subscriberCount', { count: subs }),
          items: t('marketplace.common.itemCount', { count: items }),
        }),
    }),
    [t],
  );

  const headerLabels = useMemo<MarketplaceHeaderMoleculeLabels>(
    () => ({
      title: t('marketplace.landing.title'),
      subtitle: t('marketplace.landing.subtitle'),
      myChannels: t('marketplace.landing.myChannels'),
      newChannel: t('marketplace.landing.newChannel'),
      publishers: t('marketplace.landing.publishers'),
    }),
    [t],
  );

  const handlePublishersClick = useCallback(() => {
    navigate('/marketplace/publishers');
  }, [navigate]);

  const handleMyChannelsToggle = useCallback(() => {
    setSubState((prev) => (prev === 'owned' ? 'all' : 'owned'));
  }, []);

  const heroSubscriptionStates = useMemo<
    Record<string, SubscribeButtonState>
  >(() => {
    const out: Record<string, SubscribeButtonState> = {};
    for (const channel of featuredChannels) {
      out[channel.id] = deriveSubscribeButtonState({
        isOwner: channel.owner_tenant_id === organizationId,
        status: channel.status,
        isSubscribed: !!channel.is_subscribed,
        isMandatory: !!channel.is_mandatory,
      });
    }
    return out;
  }, [featuredChannels, organizationId]);

  const handleHeroSubscribeClick = useCallback(
    (channelId: string) => {
      const channel = featuredChannels.find((c) => c.id === channelId);
      const isCurrentlySubscribed = !!channel?.is_subscribed;
      handleSubscribeToggle(channelId, isCurrentlySubscribed);
    },
    [featuredChannels, handleSubscribeToggle],
  );

  const resolveOwnerLabel = useCallback(
    (channel: ListedChannel): string | undefined => {
      const currentOrgName = (
        impersonationDetails as { organizationName?: string } | null | undefined
      )?.organizationName;
      const ownerOrgName = (
        channel as ListedChannel & { owner_org_name?: string }
      ).owner_org_name;
      if (channel.owner_tenant_id === organizationId && currentOrgName) {
        return t('marketplace.row.byYou', { org: currentOrgName });
      }
      if (ownerOrgName) {
        return t('marketplace.row.by', { org: ownerOrgName });
      }
      return undefined;
    },
    [impersonationDetails, organizationId, t],
  );

  const renderChannel = (channel: ListedChannel) => {
    const isOwner = isPublicMode ? false : channel.owner_tenant_id === organizationId;
    const isSubscribed = isPublicMode ? false : !!channel.is_subscribed;
    const isMandatory = isPublicMode ? false : !!channel.is_mandatory;
    const metrics = isPublicMode ? null : (metricsMap[`channel:${channel.id}`] ?? null);

    const ownerLabel = isPublicMode ? undefined : resolveOwnerLabel(channel);

    const subscriberCount = channel.subscriber_count;
    const itemCount = channel.item_count;
    const subsText =
      subscriberCount == null
        ? undefined
        : t('marketplace.common.subscriberCount', { count: subscriberCount });
    const itemsText =
      itemCount == null
        ? undefined
        : t('marketplace.common.itemCount', { count: itemCount });
    const subsItemsLabel =
      subsText && itemsText
        ? t('marketplace.common.statsLine', {
            subs: subsText,
            items: itemsText,
          })
        : subsText ?? itemsText;

    return (
      <ChannelCardMolecule
        key={channel.id}
        channel={channel}
        density={density}
        itemCount={itemCount ?? 0}
        subscriberCount={subscriberCount}
        metrics={metrics}
        isSubscribed={isSubscribed}
        isMandatory={isMandatory}
        isOwner={isOwner}
        ownerLabel={ownerLabel}
        subsItemsLabel={subsItemsLabel}
        subscriptionLoading={isPublicMode ? false : isToggling(channel.id)}
        onSubscribeToggle={isPublicMode ? undefined : handleSubscribeToggle}
        onClick={handleChannelClick}
      />
    );
  };

  const listClasses = [
    'marketplace-marketplace-home__results',
    `marketplace-marketplace-home__results--${density}`,
  ].join(' ');

  const hasNoActiveFilters =
    scope === 'all' && subState === 'all' && useCaseTags.length === 0;
  const showHero = hasNoActiveFilters && !isSearching;
  const featuredForHero = showHero ? featuredChannels : [];

  const sectionHeading = (() => {
    if (subState === 'subscribed' && scope === 'all') {
      return t('marketplace.landing.sectionHeading.subscribed');
    }
    if (subState === 'available' && scope === 'all') {
      return t('marketplace.landing.sectionHeading.available');
    }
    if (subState === 'owned' && scope === 'all') {
      return t('marketplace.landing.sectionHeading.owned');
    }
    if (scope !== 'all') {
      return t(`marketplace.landing.sectionHeading.${scope}`);
    }
    return t('marketplace.landing.sectionHeading.all');
  })();

  return (
    <div className="marketplace-marketplace-home">
      {!isPublicMode && (
        <MarketplaceHeaderMolecule
          labels={headerLabels}
          myChannelsCount={ownedCount}
          myChannelsActive={isOwnedFilter}
          onMyChannelsToggle={handleMyChannelsToggle}
          onNewChannelClick={handleCreateClick}
          onPublishersClick={handlePublishersClick}
        />
      )}

      {!isPublicMode && (
        <MarketplaceFilterBarMolecule
          search={searchInput}
          sort={sort}
          density={density}
          scope={scope}
          subState={subState}
          onSearchChange={handleSearchChange}
          onSortChange={setSort}
          onDensityChange={setDensity}
          onScopeChange={setScope}
          onSubStateChange={setSubState}
          labels={filterBarLabels}
        />
      )}

      {!isPublicMode && showHero && featuredForHero.length > 0 && (
        <FeaturedHeroCarouselMolecule
          channels={featuredForHero}
          onSelect={handleChannelClick}
          onSubscribeClick={handleHeroSubscribeClick}
          labels={heroLabels}
          subscriptionStates={heroSubscriptionStates}
          ratingAverages={ratingAverages}
          ratingCountLabels={ratingCountLabels}
          variant="center"
        />
      )}

      {useCasesError && (
        <p className="marketplace-marketplace-home__notice" role="alert">
          {t('marketplace.useCases.loadError')}
        </p>
      )}
      {useCasesLoading && !useCases.length && (
        <p className="marketplace-marketplace-home__notice">
          {t('marketplace.common.loading')}
        </p>
      )}

      {!isSearching && (
        <div className="marketplace-marketplace-home__results-header">
          <h2 className="marketplace-marketplace-home__results-title">
            {sectionHeading}
          </h2>
          {displayedTotal > 0 && (
            <span
              className="marketplace-marketplace-home__results-count"
              aria-live="polite"
            >
              {t('marketplace.landing.channelsCount', { count: displayedTotal })}
            </span>
          )}
        </div>
      )}

      {errorMessage && (
        <div className="marketplace-marketplace-home__error" role="alert">
          <p className="marketplace-marketplace-home__error-message">
            {t('marketplace.common.errorLoadChannels')}
          </p>
          <button
            type="button"
            className="marketplace-marketplace-home__retry-btn"
            onClick={refetchList}
          >
            {t('marketplace.landing.errorRetry')}
          </button>
        </div>
      )}

      {!isPublicMode && isSearching ? (
        <SearchResultsOrganism
          results={searchResults}
          total={searchTotal}
          loading={searchLoading}
          error={searchError}
          hasMore={searchHasMore}
          query={searchInput.trim()}
          facets={searchFacets}
          activeFilters={{}}
          sortValue={'relevance'}
          onFilterChange={() => undefined}
          onSortChange={() => undefined}
          onLoadMore={searchLoadMore}
          onChannelClick={(channelId) =>
            navigate(`/marketplace/channels/${channelId}`)
          }
          onItemClick={(item) => {
            if (item.channel_id) {
              navigate(
                `/marketplace/channels/${item.channel_id}/items/${item.item_id || item.id}`
              );
            }
          }}
          onPublisherClick={(slug) =>
            navigate(`/marketplace/publishers/${encodeURIComponent(slug)}`)
          }
          onTagClick={(tag) => setUseCaseTags([tag])}
        />
      ) : (
        <>
          {isLoading && displayedChannels.length === 0 && !errorMessage && (
            <div className="marketplace-marketplace-home__skeleton" aria-hidden="true">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="marketplace-marketplace-home__skeleton-card"
                />
              ))}
            </div>
          )}

          {!isLoading && !errorMessage && displayedChannels.length === 0 && (
            <div className="marketplace-marketplace-home__empty">
              <p className="marketplace-marketplace-home__empty-title">
                {t('marketplace.landing.empty.title')}
              </p>
              <button
                type="button"
                className="marketplace-marketplace-home__empty-action"
                onClick={handleClearFilters}
              >
                {t('marketplace.landing.empty.action')}
              </button>
            </div>
          )}

          {displayedChannels.length > 0 && (
            <div className={listClasses}>
              {displayedChannels.map(renderChannel)}
            </div>
          )}
        </>
      )}

      {!isPublicMode && (
        <CreateChannelDialogOrganism
          open={showCreateDialog}
          onClose={handleCloseCreateDialog}
          onCreated={handleChannelCreated}
          useCases={useCases}
          useCasesLoading={useCasesLoading}
          useCasesError={useCasesError}
        />
      )}
    </div>
  );
};

MarketplaceHomeOrganism.displayName = 'MarketplaceHomeOrganism';

export default MarketplaceHomeOrganism;
